import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import {
  Bell,
  BookOpen,
  BotMessageSquare,
  Building2,
  ChevronDown,
  ChevronRight,
  CircleUser,
  Gauge,
  LayoutDashboard,
  ListTodo,
  LoaderCircle,
    LogOut,
    LucideAngularModule,
    Map,
    Menu,
    PanelLeftClose,
    PanelLeftOpen,
  Radar,
    Search,
    Settings,
    ShoppingBag,
    Store,
    TvMinimalPlay,
    UserRound,
    Users,
    Waypoints,
    X,
} from 'lucide-angular';
import { AuthService } from './services/auth.service';
import { WorkspaceService } from './core/workspace.service';
import { NotificationService } from './core/notification.service';
import { ThemeService } from './core/theme.service';

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterOutlet, RouterLink, RouterLinkActive, LucideAngularModule],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
})
export class AppComponent {
    private static readonly SIDEBAR_COLLAPSED_KEY = 'acl-sidebar-collapsed';

    readonly icons = {
        bell: Bell,
        brandChevron: ChevronDown,
        breadcrumbSeparator: ChevronRight,
        collapseLeft: PanelLeftClose,
        collapseRight: PanelLeftOpen,
        credits: Gauge,
        logout: LogOut,
        user: CircleUser,
        spinner: LoaderCircle,
        menu: Menu,
        close: X,
    };

    readonly navOverview = [
        { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    ];

    readonly navSimulation = [
        { label: 'Personas', path: '/personas', icon: Users },
        { label: 'Scenarios', path: '/scenarios', icon: Waypoints },
        { label: 'Runs', path: '/runs', icon: TvMinimalPlay },
    ];

    readonly navInsights = [
        { label: 'Diagnostics', path: '/diagnostics', icon: Radar },
        { label: 'Action plans', path: '/action-plans', icon: ListTodo },
        { label: 'SEO plans', path: '/seo-plans', icon: Search },
        { label: 'Experience maps', path: '/experience-maps', icon: Map },
    ];

    readonly navAnalysis = [
        { label: 'Analyst agent', path: '/analyst-agent', icon: BotMessageSquare },
    ];

    private readonly navAgentsBase = [
        { label: 'Shopping agent', path: '/shopping-agent', icon: ShoppingBag },
        { label: 'Retail agents', path: '/retail-agents', icon: Store },
        { label: 'Human agent', path: '/human-agent', icon: UserRound },
    ];

    readonly navSystem = [
        { label: 'Brands', path: '/brands', icon: Building2 },
        { label: 'Methodology', path: '/methodology', icon: BookOpen },
        { label: 'Settings', path: '/settings', icon: Settings },
    ];

    isPublicRoute = false;
    loadingWorkspace = false;
    brandDropdownOpen = false;
    sidebarOpen = false;
    sidebarCollapsed = localStorage.getItem(AppComponent.SIDEBAR_COLLAPSED_KEY) === 'true';

    constructor(
        private readonly router: Router,
        private readonly authService: AuthService,
        public readonly workspace: WorkspaceService,
        public readonly notifications: NotificationService,
        private readonly elRef: ElementRef,
        public readonly themeService: ThemeService,
    ) {
        this.themeService.init();
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.isPublicRoute =
                    event.urlAfterRedirects.startsWith('/login') || event.urlAfterRedirects.startsWith('/register');
                this.sidebarOpen = false;
            }
        });
    }

    async ngOnInit(): Promise<void> {
        if (this.isPublicRoute) {
            return;
        }

        const isLoggedIn = await this.authService.verify();
        if (!isLoggedIn) {
            return;
        }

        this.loadingWorkspace = true;
        try {
            await this.workspace.loadContext();
            if (!this.workspace.hasWorkspace()) {
                void this.router.navigate(['/brands']);
            }
            this.notifications.start();
        } catch {
            void this.router.navigate(['/unauthorized']);
        } finally {
            this.loadingWorkspace = false;
        }
    }

    get organizationName(): string | null {
        return this.authService.getOrganizationName();
    }

    get userName(): string {
        const first = this.authService.getUserFirstName() ?? '';
        const last = this.authService.getUserLastName() ?? '';
        const lastInitial = last.charAt(0);
        return lastInitial ? `${first} ${lastInitial}.` : first;
    }

    get userInitials(): string {
        const first = (this.authService.getUserFirstName() ?? '').charAt(0);
        const last = (this.authService.getUserLastName() ?? '').charAt(0);
        return (first + last).toUpperCase() || 'U';
    }

    get navAgents() {
        return this.workspace.isPro()
            ? this.navAgentsBase
            : this.navAgentsBase.filter((item) => item.path !== '/retail-agents');
    }

    get isPro(): boolean {
        return this.workspace.isPro();
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (this.brandDropdownOpen && !this.elRef.nativeElement.querySelector('.brand-picker')?.contains(event.target)) {
            this.brandDropdownOpen = false;
        }
    }

    selectBrand(brandId: string): void {
        this.brandDropdownOpen = false;
        void this.switchBrand(brandId);
    }

    async switchBrand(brandId: string): Promise<void> {
        if (!brandId || brandId === this.workspace.activeBrand()?.id) {
            return;
        }

        this.loadingWorkspace = true;
        try {
            await this.workspace.switchBrand(brandId);
            await this.router.navigate(['/dashboard']);
        } finally {
            this.loadingWorkspace = false;
        }
    }

    toggleSidebar(): void {
        this.sidebarOpen = !this.sidebarOpen;
    }

    toggleSidebarCollapse(): void {
        this.sidebarCollapsed = !this.sidebarCollapsed;
        localStorage.setItem(AppComponent.SIDEBAR_COLLAPSED_KEY, String(this.sidebarCollapsed));
    }

    goToRuns(): void {
        void this.router.navigate(['/runs']);
    }

    creditsTooltip(): string {
        const credits = this.workspace.credits();
        if (credits.unlimited) {
            return '';
        }
        if (credits.limitReached) {
            return `Run limit reached for this organization (${credits.completedRuns}/${credits.runs}).`;
        }
        return `Organization runs used: ${credits.completedRuns}/${credits.runs} (${credits.remaining} remaining).`;
    }

    logout(): void {
        this.authService.logout().then(() => {
            this.workspace.clear();
            this.router.navigate(['/login']);
        });
    }
}
